import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Languages, LogOut, LayoutDashboard } from "lucide-react";

const LOGO =
  "https://customer-assets-cm19k8pv.emergentagent.net/wingman/4be0c1cd-8cc3-450f-ab9d-6230338a9741/attachments/dbc363331499412bba1d8b502390e8c0_image.bin";

export default function Navbar() {
  const { lang, setLang, t } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const toggleLang = () => setLang(lang === "en" ? "hi" : "en");

  const linkCls = ({ isActive }) =>
    `text-sm tracking-wide transition-colors font-bold ${
      isActive ? "text-[#0B2545]" : "text-[#0B2545] hover:text-[#C9A84C]"
    }`;

  return (
    <header
      data-testid="navbar"
      className="sticky top-0 z-50 w-full bg-white/92 backdrop-blur-md border-b border-[#EBEFF5]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 md:h-24 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 shrink-0">
          <img
            src={LOGO}
            alt="Richcube Wealth Management"
            className="h-[70px] md:h-[76px] w-auto object-contain"
          />
          <span className="hidden md:flex flex-col leading-tight">
            <span className="font-serif text-[15px] text-[#0B2545] tracking-wide">
              {t.brand}
            </span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#C9A84C]">
              {t.brand_tagline}
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          <NavLink to="/" end className={linkCls} data-testid="nav-home">
            {t.nav.home}
          </NavLink>
          <NavLink to="/properties" className={linkCls} data-testid="nav-properties">
            {t.nav.properties}
          </NavLink>
          <NavLink
            to="/properties?assured=1"
            className={linkCls}
            data-testid="nav-assured"
          >
            {t.nav.assured_return}
          </NavLink>
          <NavLink
            to="/properties?exclusive=1"
            className={linkCls}
            data-testid="nav-exclusive"
          >
            {t.nav.exclusive}
          </NavLink>
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <button
            onClick={toggleLang}
            data-testid="lang-toggle-button"
            className="inline-flex items-center gap-2 h-9 px-3 rounded-full border border-[#DFBF65] text-[#0B2545] bg-[#FFFDF5] hover:bg-[#F6EFD6] transition-colors text-xs font-semibold tracking-wider uppercase"
            aria-label="Toggle language"
          >
            <Languages size={14} />
            {lang === "en" ? "EN · हिं" : "हिं · EN"}
          </button>

          {user?.role === "admin" ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/admin")}
                data-testid="nav-admin-dashboard"
                className="hidden sm:inline-flex border-[#0B2545] text-[#0B2545] hover:bg-[#0B2545] hover:text-[#C9A84C]"
              >
                <LayoutDashboard size={14} className="mr-1.5" />
                {t.nav.dashboard}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  logout();
                  navigate("/");
                }}
                data-testid="nav-logout"
                className="text-[#1F2A44]"
              >
                <LogOut size={14} />
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              onClick={() => navigate("/admin/login")}
              data-testid="admin-login-button"
              className="bg-[#0B2545] text-[#C9A84C] hover:bg-[#133763] hover:text-[#E9D58F] font-semibold"
            >
              {t.nav.login}
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
